import { useState, useRef, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import * as constants from '../common/constants';
import * as JspUtil from '../common/JspUtil';
import * as actions from './actionCreators';
import { IceIcon, PageContainer, PageHeader } from '../components';
import { useJspVariables, useLoginFlowState, useAuth, useDebugRender, usePageTitle } from '../hooks';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { Base64 } from 'js-base64';
import Button from '@mui/material/Button';

const PageEula = () => {
    useDebugRender('PageEula');
    usePageTitle('License Agreement');
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const eulaPanelRef = useRef(null);
    const { launcher, panelStyle } = useJspVariables();

    const { EULA_DOC: eulaDocFromRedux } = useLoginFlowState();
    const { authenticatedUser } = useAuth();

    const [eulaDocFromJsp] = useState(() => {
        if (JspUtil.isValueAvailable(window.jspEulaDoc)) {
            return JSON.parse(window.jspEulaDoc);
        }
        return null;
    });

    const [viewed, setViewed] = useState(false);

    const eulaDoc = eulaDocFromJsp || eulaDocFromRedux;

    const acceptEula = async () => {
        dispatch({ type: actions.EULA_STATE, data: { popupEula: {} } });
        try {
            let resp;
            if (eulaDocFromJsp) {
                resp = await actions.acceptEulaDoc({
                    user: window.jspEulaUser,
                    app: window.jspEulaApp,
                    eulaUrl: eulaDocFromJsp.url
                });
            } else {
                resp = await actions.acceptEula(eulaDocFromRedux, launcher);
            }
            dispatch({ type: actions.AUTH_SESSION, data: { authenticatedUser: { ...authenticatedUser, ...resp.data }, isAuthenticated: true } });
            navigate(constants.ROUTE_PAGE_AUTHENTICATED);
        } catch {
            dispatch({ type: actions.AUTH_SESSION, data: { isAuthenticated: false } });
            navigate(constants.ROUTE_PAGE_LOGIN);
        }
    };

    const rejectEula = () => {
        if (JspUtil.isValueAvailable(window.jspEulaDoc)) {
            window.jspEulaDoc = null;
        }
        dispatch({ type: actions.EULA_STATE, data: { popupEula: {} } });
        dispatch({ type: actions.AUTH_SESSION, data: { authenticatedUser: {}, isAuthenticated: false } });
        dispatch({ type: actions.USER_INPUT_PASSWORD_CHANGE, data: '' });
        dispatch({ type: actions.USER_INPUT_TFA_PASSCODE_CHANGE, data: '' });
        if (JspUtil.isValueAvailable(window.jspSamlLogin)) {
            navigate(constants.ROUTE_PAGE_SAML_MESSAGE, { state: { pageMessage: "User must accept EULA to login" } });
        } else {
            navigate(constants.ROUTE_PAGE_LOGIN, { state: { pageMessage: "User must accept EULA to login" } });
        }
    };

    const checkScroll = () => {
        if (eulaPanelRef.current) {
            const { scrollTop, scrollHeight, clientHeight } = eulaPanelRef.current;
            if (scrollTop + clientHeight >= scrollHeight - 20) {
                setViewed(true);
            }
        }
    };
    
    useEffect(() => {
        checkScroll();
    }, [eulaDoc]);

    if (!eulaDoc) {
        console.error('PageEula: No EULA document available');
        return <Box>{JspUtil.getLabelFromJspVariables('err_eula_unavailable')}</Box>;
    }

    const eulaCode = eulaDoc.content ? Base64.decode(eulaDoc.content) : '';
    const frameStyle = launcher ? { height: '80%', width: '100%' } : { height: '600px', width: '100%' };

    return (
        <PageContainer id="pageEula" dataAutomationId="page_eula" sx={panelStyle}>
            {launcher?
                <Box style={panelStyle}>
                    <IceIcon launcher={launcher} />
                    <Box style={frameStyle}>
                        <Box sx={{ width: '100%', height: '60vh', overflow: 'auto' }}
                            id="eulaFrame"
                            data-automation-id="eula_frame"
                            ref={eulaPanelRef} onScroll={checkScroll}>
                            <Typography component="pre" sx={{ overflowX: 'auto', whiteSpace: 'pre-wrap', fontSize: '0.8rem', fontFamily: 'monospace', wordWrap: 'normal', wordBreak: 'normal' }}>
                                {eulaCode}
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
                        <Button variant="contained"
                            disabled={!viewed}
                            onClick={acceptEula}
                            data-automation-id="eula_accept_btn">
                            {JspUtil.getLabelFromJspVariables('action_accept')}
                        </Button>
                        <Button variant="contained"
                            onClick={rejectEula}
                            data-automation-id="eula_reject_btn">
                            {JspUtil.getLabelFromJspVariables('action_reject')}
                        </Button>
                    </Box>
                </Box>
                :
                <Box style={panelStyle}>
                    <PageHeader dataAutomationId="eula_header">{JspUtil.getLabelFromJspVariables('title_eula')}</PageHeader>
                    <Box sx={{ width: '100%', height: '600px', overflow: 'auto' }}
                        ref={eulaPanelRef} onScroll={checkScroll}
                        id="eulaFrame" data-automation-id="eula_frame">
                        <Typography component="pre" sx={{ overflowX: 'auto', whiteSpace: 'pre-wrap', fontSize: '0.8rem', fontFamily: 'monospace', wordWrap: 'normal', wordBreak: 'normal' }}>
                            {eulaCode}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={viewed}
                                    onChange={e => setViewed(e.target.checked)}
                                    name="viewed"
                                    data-automation-id="viewed"
                                />
                            }
                            label={JspUtil.getLabelFromJspVariables('label_read_eula')}
                        />
                        <Button variant="contained"
                            disabled={!viewed}
                            onClick={acceptEula}
                            data-automation-id="eula_accept_btn">
                            {JspUtil.getLabelFromJspVariables('action_accept')}
                        </Button>
                        <Button variant="contained"
                            onClick={rejectEula}
                            data-automation-id="eula_reject_btn">
                            {JspUtil.getLabelFromJspVariables('action_reject')}
                        </Button>
                    </Box>
                </Box>
            }
        </PageContainer>
    );
};

export default PageEula;
